import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useAuth } from "context/AuthContext";
import {
  getApplicationKind,
  getUserApplications,
  isAdminUser,
} from "modules/systems/services/application.service";
import { getToken } from "services/auth.service";

const ApplicationGuard = ({
  children,
  requireApplication,
  redirectTo = "/portal",
}) => {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadApplications = async () => {
      if (!user?.userId) {
        setLoading(false);
        return;
      }

      if (isAdminUser(user)) {
        setApplications([]);
        setLoading(false);
        return;
      }

      try {
        const data = await getUserApplications(user.userId);
        if (isMounted) {
          setApplications(data);
          setFailed(false);
        }
      } catch (error) {
        if (isMounted) {
          setFailed(true);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadApplications();

    return () => {
      isMounted = false;
    };
  }, [user]);

  const hasApplication = useMemo(() => {
    if (isAdminUser(user)) return true;
    if (!requireApplication) return true;

    return applications.some(
      (application) => getApplicationKind(application) === requireApplication
    );
  }, [applications, requireApplication, user]);

  const token = getToken();
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="240px"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Đang kiểm tra quyền truy cập ứng dụng...
        </Typography>
      </Box>
    );
  }

  if (failed || !hasApplication) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

export default ApplicationGuard;
